export const getDateLabel = (dateString: string): string => {
  const [datePart] = dateString.split('#');

  const parts = datePart.split('-');
  const year = Number.parseInt(parts[0] ?? '', 10);
  const month = Number.parseInt(parts[1] ?? '', 10);
  const day = Number.parseInt(parts[2] ?? '', 10);

  const showDate = Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
    ? new Date(year, month - 1, day)
    : new Date(NaN);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to compare dates only
  today.setHours(0, 0, 0, 0);
  yesterday.setHours(0, 0, 0, 0);
  if (Number.isFinite(showDate.getTime())) {
    showDate.setHours(0, 0, 0, 0);
  }

  if (showDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (showDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    if (!Number.isFinite(showDate.getTime())) {
      return 'Unknown date';
    }
    return showDate.toLocaleDateString('en-GB', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long',
      year: showDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }
};

export const getDateSortValue = (dateString: string): number => {
  const [datePartRaw, timePartRaw] = dateString.split('#');
  if (!datePartRaw) {
    return 0;
  }

  const dateParts = datePartRaw.split('-');
  const year = Number.parseInt(dateParts[0] ?? '', 10);
  const month = Number.parseInt(dateParts[1] ?? '', 10);
  const day = Number.parseInt(dateParts[2] ?? '', 10);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return 0;
  }

  // Expected format: HH:MM or HH:MM:SS (ignore any trailing timezone suffixes)
  const match = (timePartRaw ?? '').match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  const hour = match ? Number.parseInt(match[1], 10) : 0;
  const minute = match ? Number.parseInt(match[2], 10) : 0;
  const second = match && match[3] ? Number.parseInt(match[3], 10) : 0;

  const time = Date.UTC(year, month - 1, day, hour, minute, second);
  return Number.isFinite(time) ? time : 0;
};

export const groupShowsByDate = <T extends { date_start_time: string }>(
  shows: T[], 
  sortOrder: 'newest-first' | 'oldest-first' = 'newest-first'
) => {
  // Sort shows by date based on specified order
  const sortedShows = [...shows].sort((a, b) => {
    const aValue = getDateSortValue(a.date_start_time);
    const bValue = getDateSortValue(b.date_start_time);
    
    return sortOrder === 'newest-first' 
      ? bValue - aValue  // Descending (newest first)
      : aValue - bValue; // Ascending (oldest first)
  });

  // Group by date
  const grouped = sortedShows.reduce((acc, show) => {
    const dateLabel = getDateLabel(show.date_start_time);
    if (!acc[dateLabel]) {
      acc[dateLabel] = [];
    }
    acc[dateLabel].push(show);
    return acc;
  }, {} as Record<string, T[]>);

  return grouped;
};
